import { integrations, policyLimits, portfolioStats, reports, team } from "@/mocks";
import type {
  BackendIdentity,
  GeneratedReport,
  Integration,
  PolicyLimit,
  PortfolioStat,
  ReportSummary,
  TeamMember,
} from "@/lib/types";
import { apiGet, type ApiFailure } from "./api-client";

/**
 * Data with a live backend behind it.
 *
 * `auth`, `integrations` and `reports` exist on the FastAPI service today, so
 * these read from it. The case workspace has no server-side counterpart yet and
 * still runs on `store.ts`.
 *
 * Each function returns the data *and* the failure that stopped it, so a page
 * can render sample data while telling the analyst plainly that is what they
 * are looking at. Silently showing fabricated figures as though they were real
 * would be the worst outcome in a credit product.
 *
 * None of these takes a token. The session is an httpOnly cookie the browser
 * attaches itself, so there is nothing for a caller to hold or pass on.
 */
export interface Sourced<T> {
  data: T;
  /** Null when the data came from the backend. */
  fallback: ApiFailure | null;
}

// -- shapes the backend actually returns ------------------------------------

interface ConnectionResponse {
  id: string;
  provider_key: string;
  display_name: string;
  status: string;
  config: Record<string, string>;
}

interface ReportResponse {
  id: string;
  title: string;
  status: string;
  format: string;
  record_count: number;
  created_at: string;
  completed_at: string | null;
  error_code: string | null;
}

interface CurrentUserResponse {
  id: string;
  organization_id: string;
  email: string | null;
  permissions: string[];
}

// -- mappers ----------------------------------------------------------------

/** Provider keys are slugs; the UI wants something an analyst would say. */
function titleiseProvider(key: string): string {
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const CONNECTED = new Set(["active", "connected", "ready"]);
const FAILED = new Set(["error", "failed", "revoked"]);

/**
 * What kind of evidence a provider supplies.
 *
 * The backend has no category concept — a connection knows only its provider
 * key — but an underwriter groups sources by what they prove, so the mapping
 * lives here. An unknown provider falls back to its titleised key rather than
 * being labelled wrongly.
 */
const PROVIDER_CATEGORY: Record<string, string> = {
  plaid: "Banking",
  teller: "Banking",
  quickbooks_online: "Accounting",
  quickbooks: "Accounting",
  xero: "Accounting",
  sage: "Accounting",
  experian: "Bureau",
  equifax: "Bureau",
  dun_bradstreet: "Bureau",
  google_drive: "Documents",
  dropbox: "Documents",
  sharepoint: "Documents",
  salesforce: "CRM",
  hubspot: "CRM",
  sample: "Sample",
};

function toIntegration(connection: ConnectionResponse): Integration {
  const status: Integration["status"] = CONNECTED.has(connection.status)
    ? "connected"
    : FAILED.has(connection.status)
      ? "error"
      : "available";

  return {
    id: connection.id,
    name: connection.display_name || titleiseProvider(connection.provider_key),
    category:
      PROVIDER_CATEGORY[connection.provider_key] ?? titleiseProvider(connection.provider_key),
    status,
    detail: connection.config.description ?? `Provider ${connection.provider_key}.`,
    lastSync: connection.config.last_sync_at,
  };
}

function toGeneratedReport(report: ReportResponse): GeneratedReport {
  return {
    id: report.id,
    title: report.title,
    status: report.status,
    format: report.format,
    recordCount: report.record_count,
    createdAt: report.created_at,
    completedAt: report.completed_at,
    errorCode: report.error_code,
  };
}

// -- reads ------------------------------------------------------------------

export async function listIntegrations(): Promise<Sourced<Integration[]>> {
  const result = await apiGet<ConnectionResponse[]>("/api/v1/integrations/connections");
  return result.ok
    ? { data: result.data.map(toIntegration), fallback: null }
    : { data: integrations, fallback: result.failure };
}

export async function listGeneratedReports(): Promise<Sourced<GeneratedReport[]>> {
  const result = await apiGet<ReportResponse[]>("/api/v1/reports");
  return result.ok
    ? { data: result.data.map(toGeneratedReport), fallback: null }
    : { data: [], fallback: result.failure };
}

export async function getBackendIdentity(): Promise<Sourced<BackendIdentity | null>> {
  const result = await apiGet<CurrentUserResponse>("/api/v1/auth/me");
  return result.ok
    ? {
        data: {
          id: result.data.id,
          organizationId: result.data.organization_id,
          email: result.data.email,
          permissions: result.data.permissions,
        },
        fallback: null,
      }
    : { data: null, fallback: result.failure };
}

// -- still mock-only --------------------------------------------------------

/** The catalogue of report types the platform offers. Not yet a backend concept. */
export function listReports(): Promise<ReportSummary[]> {
  return Promise.resolve(reports);
}

export function getPortfolioStats(): Promise<PortfolioStat[]> {
  return Promise.resolve(portfolioStats);
}

export function listTeam(): Promise<TeamMember[]> {
  return Promise.resolve(team);
}

export function listPolicyLimits(): Promise<PolicyLimit[]> {
  return Promise.resolve(policyLimits);
}
